/**
 * ALTIORA · Central organisation contact — MUST be the single source of truth
 * for the founder name, phone, emails, and website across the entire app.
 *
 * The values here mirror `backend/core/org_contact.py` so we can render
 * legal pages, footers, mailto/tel links, and generated documents in a
 * consistent way — WITHOUT a network call. For dynamic overrides, use
 * `useOrgContact()` which hits GET /api/org/contact.
 *
 * Rule: never hardcode an email, phone, or founder name outside of this
 * file. Always import ORG_CONTACT or use useOrgContact().
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const ORG_CONTACT = {
  legal_name: "ALTIORA COOP",
  display_name: "ALTIORA COOP",
  brand_name: "ELIA",

  founder: {
    full_name: "Romain TOUSSE MELATAGUIA",
    title_fr: "Fondateur",
    title_en: "Founder",
  },

  phone: {
    e164: "+15814434803",
    display: "+1 581 443 4803",
    tel_href: "tel:+15814434803",
  },

  emails: {
    contact:  "contact@altioracoop.com",
    support:  "support@altioracoop.com",
    partners: "partenaires@altioracoop.com",
    finance:  "finance@altioracoop.com",
  },

  website: "https://altioracoop.com",

  address: {
    fr: "Québec, Canada",
    en: "Quebec, Canada",
  },
};

/** Return an anchor href for the given email key (contact | support | partners | finance). */
export function mailto(key = "contact", subject = null) {
  const addr = ORG_CONTACT.emails[key] || ORG_CONTACT.emails.contact;
  return subject ? `mailto:${addr}?subject=${encodeURIComponent(subject)}` : `mailto:${addr}`;
}

/** Return the tel: href for the platform's official phone number. */
export function telHref() {
  return ORG_CONTACT.phone.tel_href;
}

/** Hook: fetch the live config from the backend (env-overridable), fallback to constants. */
export function useOrgContact() {
  const [org, setOrg] = useState(ORG_CONTACT);
  useEffect(() => {
    let cancelled = false;
    api.get("/org/contact", { auth: false })
      .then((r) => { if (!cancelled && r?.data) setOrg(r.data); })
      .catch(() => { /* keep local fallback */ });
    return () => { cancelled = true; };
  }, []);
  return org;
}
